import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface GalleryItem {
  id: string;
  title: string;
  description: string;
  before_photo_url: string;
  after_photo_url: string;
  service_date: string;
  location: string;
  is_featured: boolean;
}

export default function GalleryPage() {
  const [scrolled, setScrolled] = useState(false);
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    const { data, error } = await supabase
      .from('gallery')
      .select('*')
      .order('is_featured', { ascending: false })
      .order('service_date', { ascending: false });

    if (!error && data) {
      setGallery(data);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-yellow-50">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl md:text-6xl font-black text-blue-600 mb-4" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              Before &amp; After Gallery
            </h1>
            <p className="text-xl text-gray-600">
              See the amazing transformations our crew delivers!
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                Loading gallery...
              </div>
            </div>
          ) : gallery.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-2xl font-bold text-gray-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                Gallery coming soon!
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gallery.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  onClick={() => setSelectedItem(item)}
                  className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer"
                >
                  {item.is_featured && (
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-center py-2 font-bold text-sm">
                      ⭐ Featured Work
                    </div>
                  )}
                  
                  <div className="relative">
                    <div className="grid grid-cols-2">
                      <div className="relative w-full h-48">
                        <img
                          src={item.before_photo_url}
                          alt={`${item.title} - Before`}
                          className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-center py-1 text-xs font-bold">
                          BEFORE
                        </div>
                      </div>
                      <div className="relative w-full h-48">
                        <img
                          src={item.after_photo_url}
                          alt={`${item.title} - After`}
                          className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-green-500 text-white text-center py-1 text-xs font-bold">
                          AFTER
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line"></i>
                        {item.location}
                      </span>
                      <span>
                        {new Date(item.service_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {selectedItem && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedItem(null)}
              className="fixed inset-0 bg-black/80 z-40 cursor-pointer"
            ></motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                <div className="relative">
                  <button
                    onClick={() => setSelectedItem(null)}
                    className="absolute top-4 right-4 w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer z-10"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>

                  <div className="p-8">
                    <h2 className="text-3xl font-black text-blue-600 mb-4" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                      {selectedItem.title}
                    </h2>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <div className="bg-red-500 text-white text-center py-2 rounded-t-2xl font-bold">
                          BEFORE
                        </div>
                        <div className="relative w-full h-80">
                          <img
                            src={selectedItem.before_photo_url}
                            alt="Before"
                            className="w-full h-full object-cover object-top rounded-b-2xl"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="bg-green-500 text-white text-center py-2 rounded-t-2xl font-bold">
                          AFTER
                        </div>
                        <div className="relative w-full h-80">
                          <img
                            src={selectedItem.after_photo_url}
                            alt="After"
                            className="w-full h-full object-cover object-top rounded-b-2xl"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <p className="text-gray-700 leading-relaxed">{selectedItem.description}</p>
                      <div className="flex items-center gap-6 text-sm text-gray-600">
                        <span className="flex items-center gap-2">
                          <i className="ri-map-pin-fill text-red-500"></i>
                          {selectedItem.location}
                        </span>
                        <span className="flex items-center gap-2">
                          <i className="ri-calendar-fill text-blue-600"></i>
                          {new Date(selectedItem.service_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}